import { useState } from 'react';

import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import { useCasAuthenticated } from 'src/compass/shared/hooks/useCasAuthenticated';
import { AppPermissions, useUserHasPermission } from 'src/compass/site/routes/useUserPermissions';

export const PENDING_EMAILS_QUERY_KEY = 'Msg/History/Pending';

const POLL_MS = 3000;

export enum PendingEmailStatus {
  Requested = 'Requested',
  Failed = 'Failed',
}

/** Age past which a still-sending email is called out as taking longer than it should. */
export const STALE_AFTER_SECONDS = 60;

export interface IPendingEmail {
  id: number;
  subject: string;
  dateTime: string;
  /** Server-computed; see MsgHistoryPendingResponseBo.AgeSeconds for why it isn't derived here. */
  ageSeconds: number;
  status: PendingEmailStatus;
  errorMessage: string | null;
  to: string[];
}

export function usePendingEmails() {
  const { isAuthenticated } = useCasAuthenticated();
  const canSeeSceReports = useUserHasPermission(AppPermissions.SCE_REPORTS);
  const [hasPending, setHasPending] = useState(false);

  const query = useRetrieve({
    queryKey: [PENDING_EMAILS_QUERY_KEY],
    url: PENDING_EMAILS_QUERY_KEY,
    // The indicator is mounted app-wide but the endpoint is SCE_REPORTS-scoped, so without this
    // gate every user without that permission would 403 on each poll and onErrorDefaultHandler
    // would turn it into snackbar spam.
    enabled: isAuthenticated && canSeeSceReports,
    // Catches an email sent from another tab, and returning to the app. Needed because once the
    // interval below goes false nothing else restarts it (the same-tab case is covered by the
    // send dialog invalidating this query).
    refetchOnWindowFocus: true,
    // Poll only while something is actually pending. With nothing pending this is false and the
    // query goes idle, so a user who isn't mid-send makes one request per page load and then
    // stops - this must never become a permanent per-user poller.
    refetchInterval: hasPending ? POLL_MS : false,
    onSuccess: (response: any) => {
      setHasPending((response?.result?.length ?? 0) > 0);
      // useAPI runs onSuccess inside queryFn and caches whatever it returns, so this must return
      // the response rather than only firing the side effect above.
      return response;
    },
  });

  const rows: IPendingEmail[] = query.data?.result ?? [];
  const errorCount = rows.filter((row) => row.status === PendingEmailStatus.Failed).length;

  return {
    rows,
    count: rows.length,
    // Counted separately from `count`: the error label reports how many failed, not how many are
    // listed, so one failure alongside seven in-flight reads "Error sending", not "(8)".
    errorCount,
    sendingCount: rows.length - errorCount,
    hasError: errorCount > 0,
  };
}
